import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MagnifyingGlass, User, Heart, ShoppingCart, List as ListIcon, X, Phone, CaretDown, CalendarCheck, SignOut, Sun, Eyeglasses, Drop, DeviceMobile, Tag, House, Storefront, UsersThree, Newspaper, Envelope } from '@phosphor-icons/react';
import { useCart } from '../../context/CartContext';
import { useWishlist } from '../../context/WishlistContext';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { OpeningStatus } from './components/openingHours';

const DEFAULT_STORE_SLUG = 'mrg';
const API_URL = process.env.REACT_APP_BACKEND_URL;

export const MrgHeader = ({ store, categories = [], onBoutiqueClick, onCartClick, onHomeClick, standalone = false, onCategorySelect }) => {
  const storeSlug = DEFAULT_STORE_SLUG;
  const navigate = useNavigate();
  const { itemCount, setIsOpen, setStoreSlug } = useCart();
  const { wishlistCount, fetchWishlistBySlug } = useWishlist();
  const { user, logout } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mega, setMega] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [megaTabSlug, setMegaTabSlug] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [brands, setBrands] = useState([]);

  useEffect(() => { if (storeSlug) setStoreSlug(storeSlug); }, [storeSlug, setStoreSlug]);
  useEffect(() => { if (storeSlug) fetchWishlistBySlug?.(storeSlug); }, [storeSlug, fetchWishlistBySlug]);
  useEffect(() => {
    axios.get(`${API_URL}/api/s/mrg/brands?with_image=false`).then(r => setBrands((r.data || []).slice(0, 10))).catch(() => {});
  }, []);

  useEffect(() => {
    const q = searchQuery.trim();
    if (!searchOpen || q.length < 2) { setSearchResults([]); return undefined; }
    const t = setTimeout(() => {
      axios.get(`${API_URL}/api/s/mrg/search?q=${encodeURIComponent(q)}&limit=6`)
        .then(r => setSearchResults(r.data?.products || []))
        .catch(() => setSearchResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [searchQuery, searchOpen]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (!store) return null;
  const colors = store?.theme_config?.colors || { primary: '#1B2A4A', accent: '#C9A24B' };
  const primary = colors.primary || '#1B2A4A';
  const accent = colors.accent || '#C9A24B';
  const id = store?.identity || {};
  const logoUrl = id.logo_url || id.logo_light || store?.theme_config?.logo_url;
  const phone = id.contact_phone || '';

  const findCat = (slugPart) => (categories || []).find(c => (c.slug || '').includes(slugPart));
  const solaires = findCat('soleil');
  const vue = findCat('vue');
  const lentilles = findCat('lentille');
  const connectees = findCat('connect');
  const sportVue = (categories || []).find(c => c.slug === 'lunettes-de-sport-a-la-vue');
  const boutiqueCols = [
    { label: 'Lunettes de soleil', cat: solaires, desc: 'Toutes nos solaires', highlight: false },
    { label: 'Lunettes de vue', cat: vue, desc: 'Montures optiques', highlight: false },
    { label: 'Lunettes de sport à la vue', cat: sportVue, desc: 'Adaptées à votre correction', highlight: true },
    { label: 'Lentilles', cat: lentilles, desc: 'Lentilles de contact', highlight: false },
    { label: 'Lunettes connectées', cat: connectees, desc: 'Ray-Ban Meta & co', highlight: false },
  ].filter(x => x.cat);
  const megaTabs = boutiqueCols.map(c => ({ name: c.label, slug: c.cat.slug, id: c.cat.id, highlight: c.highlight }));
  const activeMegaTab = megaTabs.find(t => t.slug === megaTabSlug) || megaTabs[0];

  const goCat = (cat) => {
    setMobileOpen(false);
    if (!cat) { (onBoutiqueClick ? onBoutiqueClick() : navigate('/')); return; }
    if (onCategorySelect) onCategorySelect(cat.id);
    else navigate(`/?cat=${cat.id}`);
  };

  const handleCart = () => { setMobileOpen(false); onCartClick ? onCartClick() : setIsOpen(true); };
  const handleHome = () => { setMobileOpen(false); onHomeClick ? onHomeClick() : navigate('/'); };
  const handleLogout = async () => { await logout(); navigate('/'); };

  const goAnchor = (id) => {
    setMobileOpen(false);
    const doScroll = () => { const el = document.getElementById(id); if (el) el.scrollIntoView({ behavior: 'smooth' }); };
    if (window.location.pathname === '/') doScroll();
    else { navigate('/'); setTimeout(doScroll, 450); }
  };

  const mobileBoutique = [
    solaires && { label: 'Lunettes de soleil', icon: <Sun size={18} weight="fill" />, onClick: () => goCat(solaires) },
    vue && { label: 'Lunettes de vue', icon: <Eyeglasses size={18} weight="fill" />, onClick: () => goCat(vue) },
    lentilles && { label: 'Lentilles', icon: <Drop size={18} weight="fill" />, onClick: () => goCat(lentilles) },
    connectees && { label: 'Lunettes connectées', icon: <DeviceMobile size={18} weight="fill" />, onClick: () => goCat(connectees) },
    { label: 'Toutes les marques', icon: <Tag size={18} weight="fill" />, onClick: () => { setMobileOpen(false); navigate('/marques'); } },
  ].filter(Boolean);

  const mobileContent = [
    { label: 'Accueil', icon: <House size={18} weight="fill" />, onClick: handleHome },
    { label: 'Le magasin', icon: <Storefront size={18} weight="fill" />, onClick: () => goAnchor('notre-boutique') },
    { label: 'Notre équipe', icon: <UsersThree size={18} weight="fill" />, onClick: () => goAnchor('notre-equipe') },
    { label: 'Nos conseils', icon: <Newspaper size={18} weight="fill" />, onClick: () => { setMobileOpen(false); navigate('/blog'); } },
    { label: 'Nous contacter', icon: <Envelope size={18} weight="fill" />, onClick: () => { setMobileOpen(false); navigate('/page/contact'); } },
  ];

  return (
    <header className="sticky top-0 left-0 right-0 z-50" data-testid="mrg-header" style={{ fontFamily: "'Poppins', sans-serif" }}>
      {/* Announcement bar */}
      <div className="text-white text-[12.5px]" style={{ backgroundColor: primary }}>
        <div className="max-w-[1320px] mx-auto px-5 h-9 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <OpeningStatus closures={store?.settings?.holiday_closures || []} accent={accent} />
            <span className="hidden md:inline text-white/25">·</span>
            <span className="hidden md:inline text-white/65 truncate">Opticien à Istres — Tiers payant mutuelles</span>
          </div>
          <div className="flex items-center gap-4 ml-auto shrink-0">
            <Link to="/marques" className="hidden md:inline text-white/70 hover:text-white transition-colors">Toutes les marques</Link>
            {phone && <a href={`tel:${phone.replace(/\s/g,'')}`} className="flex items-center gap-1.5 text-white/90 hover:text-white"><Phone size={13} weight="fill" style={{ color: accent }} /> {phone}</a>}
          </div>
        </div>
      </div>

      {/* Main bar */}
      <div className={`bg-white transition-shadow relative ${scrolled ? 'shadow-md' : 'shadow-sm'}`} onMouseLeave={() => { setMega(false); setAboutOpen(false); }}>
        <div className="max-w-[1320px] mx-auto px-3 sm:px-5 h-[64px] md:h-[74px] flex items-center gap-2 sm:gap-3 md:gap-4">
          {/* Mobile burger */}
          <button className="lg:hidden text-slate-800 -ml-1 shrink-0" onClick={() => setMobileOpen(true)} data-testid="mrg-mobile-burger" aria-label="Menu"><ListIcon size={24} weight="bold" /></button>

          {/* Logo */}
          <button onClick={handleHome} className="flex items-center shrink-0 min-w-0" data-testid="mrg-logo">
            {logoUrl
              ? <img src={logoUrl} alt={store.name} className="h-7 sm:h-9 md:h-11 w-auto object-contain max-w-[150px] sm:max-w-none" />
              : <span className="text-[20px] sm:text-[22px] font-semibold tracking-tight" style={{ color: primary }}>MRG</span>}
          </button>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1 mx-auto">
            <div onMouseEnter={() => { setMega(true); setAboutOpen(false); }}>
              <button onClick={() => (onBoutiqueClick ? onBoutiqueClick() : navigate('/'))}
                className="px-3 py-2 text-[14px] font-semibold text-slate-800 flex items-center gap-1.5 whitespace-nowrap" data-testid="nav-boutique">
                Boutique <CaretDown size={12} weight="bold" className="transition-transform" style={{ transform: mega ? 'rotate(180deg)' : 'none' }} />
              </button>
            </div>
            <button onClick={() => { setMega(false); navigate('/marques'); }} onMouseEnter={() => setMega(false)}
              className="px-3 py-2 text-[14px] font-medium text-slate-700 hover:text-slate-900 whitespace-nowrap relative group" data-testid="nav-marques">
              Marques<span className="absolute left-3.5 right-3.5 -bottom-0.5 h-[2px] scale-x-0 group-hover:scale-x-100 transition-transform origin-left" style={{ backgroundColor: accent }} />
            </button>
            <button onClick={() => { setMega(false); navigate('/blog'); }} onMouseEnter={() => setMega(false)}
              className="px-3 py-2 text-[14px] font-medium text-slate-700 hover:text-slate-900 whitespace-nowrap relative group" data-testid="nav-conseils">
              Nos conseils<span className="absolute left-3.5 right-3.5 -bottom-0.5 h-[2px] scale-x-0 group-hover:scale-x-100 transition-transform origin-left" style={{ backgroundColor: accent }} />
            </button>
            <div onMouseEnter={() => { setAboutOpen(true); setMega(false); }} className="relative">
              <button className="px-3 py-2 text-[14px] font-medium text-slate-700 hover:text-slate-900 whitespace-nowrap flex items-center gap-1.5" data-testid="nav-about">
                À propos <CaretDown size={12} weight="bold" className="transition-transform" style={{ transform: aboutOpen ? 'rotate(180deg)' : 'none' }} />
              </button>
              {aboutOpen && (
                <div className="absolute left-0 top-full pt-3 w-56 z-50">
                  <div className="bg-white rounded-xl shadow-xl border border-slate-100 py-2">
                    <button onClick={() => { setAboutOpen(false); const el = document.getElementById('notre-boutique'); el ? el.scrollIntoView({ behavior: 'smooth' }) : navigate('/'); }} className="block w-full text-left px-4 py-2.5 text-[13.5px] text-slate-700 hover:bg-slate-50">Le magasin</button>
                    <button onClick={() => { setAboutOpen(false); const el = document.getElementById('notre-equipe'); el ? el.scrollIntoView({ behavior: 'smooth' }) : navigate('/'); }} className="block w-full text-left px-4 py-2.5 text-[13.5px] text-slate-700 hover:bg-slate-50">Notre équipe</button>
                    <Link to="/page/contact" onClick={() => setAboutOpen(false)} className="block px-4 py-2.5 text-[13.5px] text-slate-700 hover:bg-slate-50">Nous contacter</Link>
                    <Link to="/blog" onClick={() => setAboutOpen(false)} className="block px-4 py-2.5 text-[13.5px] text-slate-700 hover:bg-slate-50">Notre actu</Link>
                  </div>
                </div>
              )}
            </div>
          </nav>

          {/* Right icons */}
          <div className="flex items-center gap-0.5 sm:gap-1.5 md:gap-3 ml-auto lg:ml-0">
            <button onClick={() => setSearchOpen(s => !s)} className="text-slate-700 hover:text-slate-900 p-1.5" aria-label="Rechercher" data-testid="mrg-search"><MagnifyingGlass size={20} weight="regular" /></button>
            <Link to={user ? '/account' : '/login'} className="md:hidden text-slate-700 hover:text-slate-900 p-1.5" aria-label="Mon compte" data-testid="mrg-account-mobile"><User size={20} weight="regular" /></Link>
            {user ? (
              <div className="relative group hidden md:block">
                <Link to="/account" className="text-slate-700 hover:text-slate-900 p-1.5 flex items-center gap-1" data-testid="mrg-account"><User size={21} weight="regular" /><CaretDown size={11} weight="bold" /></Link>
                <div className="absolute right-0 top-full pt-2 w-48 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                  <div className="bg-white rounded-xl shadow-xl border border-slate-100 py-2">
                    <Link to="/account" className="block px-4 py-2 text-[13px] text-slate-700 hover:bg-slate-50">Mon compte</Link>
                    <Link to="/account/orders" className="block px-4 py-2 text-[13px] text-slate-700 hover:bg-slate-50">Mes commandes</Link>
                    <Link to="/account/wishlist" className="block px-4 py-2 text-[13px] text-slate-700 hover:bg-slate-50">Mes favoris</Link>
                    <button onClick={handleLogout} className="block w-full text-left px-4 py-2 text-[13px] text-rose-600 hover:bg-rose-50">Déconnexion</button>
                  </div>
                </div>
              </div>
            ) : (
              <Link to="/login" className="text-slate-700 hover:text-slate-900 p-1.5 hidden md:block" data-testid="mrg-login"><User size={21} weight="regular" /></Link>
            )}
            <Link to="/account/wishlist" className="relative text-slate-700 hover:text-slate-900 p-1.5 hidden sm:block" aria-label="Favoris">
              <Heart size={21} weight="regular" />
              {wishlistCount > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 text-white text-[9px] font-bold rounded-full flex items-center justify-center" style={{ backgroundColor: accent }}>{wishlistCount}</span>}
            </Link>
            <button onClick={handleCart} className="relative flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 md:w-11 md:h-11 rounded-full text-white shadow-md transition-transform hover:scale-105 active:scale-95 ml-0.5 shrink-0" style={{ backgroundColor: accent }} aria-label="Panier" data-testid="mrg-cart">
              <ShoppingCart size={20} className="sm:hidden" weight="bold" />
              <ShoppingCart size={24} className="hidden sm:block" weight="bold" />
              {itemCount > 0 && <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-white" style={{ backgroundColor: primary, color: '#fff' }}>{itemCount}</span>}
            </button>
            {/* Mobile RDV — icon only on xs, icon+text on sm+ */}
            <button onClick={() => navigate('/rendez-vous')} aria-label="Prendre rendez-vous"
              className="lg:hidden flex items-center justify-center gap-1.5 h-9 w-9 sm:w-auto sm:px-3.5 rounded-full text-white shrink-0 text-[12.5px] font-semibold active:scale-95 transition-transform" style={{ backgroundColor: accent }} data-testid="mrg-rdv-mobile">
              <CalendarCheck size={16} weight="bold" /> <span className="hidden sm:inline whitespace-nowrap">Prendre RDV</span>
            </button>
            <button onClick={() => navigate('/rendez-vous')} className="hidden lg:flex items-center gap-2 ml-1 px-4 py-2.5 rounded-full text-[13.5px] font-semibold whitespace-nowrap transition-transform hover:scale-105" style={{ backgroundColor: accent, color: '#ffffff' }} data-testid="mrg-rdv">
              <CalendarCheck size={17} weight="bold" /> Prendre RDV
            </button>
          </div>
        </div>

        {/* Search overlay */}
        {searchOpen && (
          <div className="absolute left-0 right-0 top-full bg-white border-t border-slate-100 shadow-lg z-50" data-testid="mrg-search-overlay">
            <form onSubmit={(e) => { e.preventDefault(); const q = searchQuery.trim(); if (q) { setSearchOpen(false); navigate(`/?q=${encodeURIComponent(q)}`); } }}
              className="max-w-[1320px] mx-auto px-5 py-3 flex items-center gap-3">
              <MagnifyingGlass size={20} className="text-slate-400 shrink-0" />
              <input autoFocus value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                placeholder="Rechercher une marque, un modèle…"
                className="flex-1 h-10 outline-none text-[15px] bg-transparent" data-testid="mrg-search-input" />
              <button type="submit" className="px-5 py-2 rounded-full text-white text-[13.5px] font-semibold shrink-0" style={{ backgroundColor: accent }}>Rechercher</button>
              <button type="button" onClick={() => setSearchOpen(false)} className="text-slate-400 hover:text-slate-700 shrink-0" aria-label="Fermer"><X size={20} weight="bold" /></button>
            </form>
            {searchResults.length > 0 && (
              <div className="max-w-[1320px] mx-auto px-5 pb-4 border-t border-slate-50 pt-3" data-testid="search-results">
                <div className="grid sm:grid-cols-2 gap-1">
                  {searchResults.map(p => (
                    <button key={p.id} onClick={() => { setSearchOpen(false); setSearchQuery(''); navigate(`/product/${p.slug}`); }}
                      className="flex items-center gap-3 p-2 rounded-xl hover:bg-slate-50 text-left" data-testid={`search-result-${p.slug}`}>
                      <div className="w-12 h-12 rounded-lg bg-slate-50 overflow-hidden flex items-center justify-center shrink-0">{p.featured_image && <img src={p.featured_image} alt="" className="w-full h-full object-contain p-1" />}</div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] font-medium text-slate-800 truncate">{p.name}</div>
                        {p.price != null && <div className="text-[12px] font-semibold" style={{ color: primary }}>{parseFloat(p.price).toFixed(2)} €</div>}
                      </div>
                    </button>
                  ))}
                </div>
                <button onClick={() => { const q = searchQuery.trim(); setSearchOpen(false); if (q) navigate(`/?q=${encodeURIComponent(q)}`); }} className="mt-2 text-[13px] font-semibold" style={{ color: accent }} data-testid="search-see-all">Voir tous les résultats →</button>
              </div>
            )}
          </div>
        )}

        {/* Mega menu panel — Boutique (tabs: type → type + brand links) */}
        {mega && activeMegaTab && (
          <div className="hidden lg:block absolute left-0 right-0 top-full bg-white border-t border-slate-100 shadow-xl z-40" onMouseEnter={() => setMega(true)}>
            <div className="max-w-[1320px] mx-auto px-5 py-6 grid grid-cols-[230px_1fr_240px] gap-7">
              {/* Tabs (types) */}
              <div className="border-r border-slate-100 pr-4">
                {megaTabs.map(t => {
                  const active = t.slug === activeMegaTab.slug;
                  return (
                    <button key={t.slug}
                      onMouseEnter={() => setMegaTabSlug(t.slug)}
                      onClick={() => { setMega(false); goCat({ id: t.id }); }}
                      className="w-full flex items-center justify-between text-left px-3 py-2.5 rounded-lg text-[14px] font-medium transition-colors mb-0.5"
                      style={{ backgroundColor: active ? accent + '12' : 'transparent', color: active ? primary : '#334155' }}
                      data-testid={`mega-tab-${t.slug}`}>
                      <span className="flex items-center gap-2">
                        {t.name}
                        {t.highlight && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded text-white" style={{ backgroundColor: accent }}>Nouveau</span>}
                      </span>
                      <CaretDown size={12} weight="bold" style={{ transform: 'rotate(-90deg)', opacity: active ? 1 : 0.4, color: active ? accent : '#94a3b8' }} />
                    </button>
                  );
                })}
                <Link to="/marques" onClick={() => setMega(false)} className="block mt-3 px-3 text-[13px] font-semibold" style={{ color: accent }}>Toutes les marques →</Link>
              </div>

              {/* Type + brand combos — OR featured panel for Lunettes connectées */}
              {activeMegaTab.slug === 'lunettes-connectees' ? (
                <div className="col-span-2 grid grid-cols-[1.1fr_1fr] gap-6 items-stretch">
                  {/* Featured video panel */}
                  <div className="relative rounded-2xl overflow-hidden bg-black min-h-[260px]" data-testid="mega-meta-feature">
                    <iframe
                      title="Ray-Ban Meta showcase"
                      src="https://www.youtube.com/embed/JDRcBmcIf98?autoplay=1&mute=1&loop=1&playlist=JDRcBmcIf98&controls=0&showinfo=0&modestbranding=1&playsinline=1&rel=0&disablekb=1"
                      className="absolute top-1/2 left-1/2 pointer-events-none border-0"
                      style={{ width: '177.78%', height: '100%', transform: 'translate(-50%, -50%)' }}
                      allow="autoplay; encrypted-media" frameBorder="0"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                    <div className="absolute inset-0 p-6 flex flex-col justify-end text-white">
                      <img src="/api/static/branding/brands/meta.svg" alt="Meta" className="h-7 w-auto mb-3 brightness-0 invert opacity-95" />
                      <h3 className="text-[22px] font-bold leading-tight mb-1.5">Ray-Ban Meta — voyez le monde autrement</h3>
                      <p className="text-[13px] text-white/75 mb-4 max-w-[400px]">Lunettes intelligentes propulsées par Meta. Photo, vidéo, appels, IA conversationnelle.</p>
                      <button onClick={() => { setMega(false); navigate('/marque/ray-ban-meta'); }}
                        className="inline-flex w-fit items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-[13px] transition-transform hover:scale-105"
                        style={{ backgroundColor: accent, color: '#fff' }}
                        data-testid="mega-cta-meta">
                        Découvrir Ray-Ban Meta →
                      </button>
                    </div>
                  </div>
                  {/* Secondary info / quick links */}
                  <div className="flex flex-col justify-between py-2">
                    <div>
                      <h4 className="text-[12px] font-bold uppercase tracking-wider mb-3" style={{ color: accent }}>Bon à savoir</h4>
                      <ul className="space-y-2.5 text-[13px] text-slate-700">
                        <li className="flex gap-2"><span className="mt-1.5 w-1 h-1 rounded-full shrink-0" style={{ background: accent }} />Capture photo et vidéo POV instantanée</li>
                        <li className="flex gap-2"><span className="mt-1.5 w-1 h-1 rounded-full shrink-0" style={{ background: accent }} />Audio open-ear pour appels et musique</li>
                        <li className="flex gap-2"><span className="mt-1.5 w-1 h-1 rounded-full shrink-0" style={{ background: accent }} />Assistant IA Meta vocal intégré</li>
                        <li className="flex gap-2"><span className="mt-1.5 w-1 h-1 rounded-full shrink-0" style={{ background: accent }} />Compatible avec verres correcteurs</li>
                      </ul>
                    </div>
                    <button onClick={() => { setMega(false); navigate('/rendez-vous'); }}
                      className="mt-5 w-full rounded-xl py-3 text-[13px] font-semibold transition-colors border" style={{ borderColor: primary + '20', color: primary }}>
                      Essayer en magasin — Prendre RDV
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <h4 className="text-[12px] font-bold uppercase tracking-wider mb-4" style={{ color: accent }}>{activeMegaTab.name} par marque</h4>
                    <div className="grid grid-cols-3 gap-x-5 gap-y-2">
                      <button onClick={() => { setMega(false); goCat({ id: activeMegaTab.id }); }}
                        className="text-left text-[13.5px] font-semibold text-slate-900 hover:underline">
                        Tout&nbsp;voir
                      </button>
                      {brands.map(b => (
                        <Link key={b.id} to={`/marque/${b.slug}?type=${activeMegaTab.slug}`} onClick={() => setMega(false)}
                          className="text-[13px] text-slate-600 hover:text-slate-900 transition-colors leading-snug"
                          data-testid={`mega-combo-${activeMegaTab.slug}-${b.slug}`}>
                          {activeMegaTab.name} <span className="font-medium">{b.name}</span>
                        </Link>
                      ))}
                    </div>
                    <button onClick={() => { setMega(false); goCat({ id: activeMegaTab.id }); }} className="mt-5 inline-flex items-center gap-1.5 text-[13.5px] font-bold" style={{ color: primary }}>
                      Voir toutes les {activeMegaTab.name.toLowerCase()} →
                    </button>
                  </div>

                  {/* Promo RDV card */}
                  <button onClick={() => { setMega(false); navigate('/rendez-vous'); }}
                    className="relative rounded-2xl overflow-hidden text-left p-6 flex flex-col justify-end min-h-[190px]" style={{ background: `linear-gradient(135deg, ${primary}, ${accent})` }}>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-white/80">Sur rendez-vous</span>
                    <span className="text-white text-[20px] font-semibold leading-tight mt-1">Examen de vue<br />& conseils experts</span>
                    <span className="text-white/85 text-[13px] mt-2 inline-flex items-center gap-1.5">Prendre rendez-vous →</span>
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[100] lg:hidden" data-testid="mrg-mobile-menu">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-[84%] max-w-[330px] bg-white overflow-y-auto">
            <div className="flex items-center justify-between px-5 h-[64px] border-b border-slate-100" style={{ backgroundColor: primary }}>
              {logoUrl ? <img src={logoUrl} alt={store.name} className="h-8 w-auto object-contain brightness-0 invert" /> : <span className="text-white font-semibold text-lg">MRG</span>}
              <button onClick={() => setMobileOpen(false)} className="text-white/80 hover:text-white"><X size={24} weight="bold" /></button>
            </div>
            <div className="px-5 py-2.5 border-b border-slate-100 bg-slate-50">
              <OpeningStatus closures={store?.settings?.holiday_closures || []} accent={accent} dark />
            </div>
            <nav className="pb-3">
              {/* Boutique en ligne */}
              <div className="px-5 pt-4 pb-1.5 text-[11px] font-bold uppercase tracking-wider" style={{ color: accent }}>Boutique en ligne</div>
              {mobileBoutique.map(it => (
                <button key={it.label} onClick={it.onClick} className="flex items-center gap-3 w-full text-left px-5 py-3 text-[15px] font-medium text-slate-800 hover:bg-slate-50">
                  <span className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: accent + '14', color: accent }}>{it.icon}</span>
                  {it.label}
                </button>
              ))}
              {/* Le magasin / pages contenu */}
              <div className="px-5 pt-4 pb-1.5 text-[11px] font-bold uppercase tracking-wider border-t border-slate-100 mt-2" style={{ color: accent }}>MRG · Le magasin</div>
              {mobileContent.map(it => (
                <button key={it.label} onClick={it.onClick} className="flex items-center gap-3 w-full text-left px-5 py-3 text-[15px] font-medium text-slate-800 hover:bg-slate-50">
                  <span className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: primary + '0D', color: primary }}>{it.icon}</span>
                  {it.label}
                </button>
              ))}
              {/* Mon compte */}
              <div className="px-5 pt-4 pb-1.5 text-[11px] font-bold uppercase tracking-wider border-t border-slate-100 mt-2" style={{ color: accent }}>Mon compte</div>
              {user ? (
                <>
                  <Link to="/account" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-5 py-3 text-[15px] text-slate-800 hover:bg-slate-50"><span className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: primary + '0D', color: primary }}><User size={17} weight="regular" /></span> Mon compte</Link>
                  <Link to="/account/wishlist" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-5 py-3 text-[15px] text-slate-800 hover:bg-slate-50"><span className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: primary + '0D', color: primary }}><Heart size={17} weight="regular" /></span> Mes favoris</Link>
                  <button onClick={handleLogout} className="flex items-center gap-3 w-full text-left px-5 py-3 text-[15px] text-rose-600"><span className="w-7 h-7 rounded-lg bg-rose-50 flex items-center justify-center shrink-0"><SignOut size={17} weight="regular" /></span> Déconnexion</button>
                </>
              ) : (
                <Link to="/login" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-5 py-3 text-[15px] font-medium text-slate-800 hover:bg-slate-50" data-testid="mobile-login"><span className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: primary + '0D', color: primary }}><User size={17} weight="regular" /></span> Connexion / Inscription</Link>
              )}
            </nav>
            <div className="px-5 py-4">
              <button onClick={() => { setMobileOpen(false); navigate('/rendez-vous'); }} className="w-full py-3 rounded-full text-[14px] font-semibold" style={{ backgroundColor: accent, color: '#ffffff' }}>Prendre rendez-vous</button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default MrgHeader;
